<!--
  @component AuthorizationDetail
  @description Detalle completo de un caso de autorización.
  Muestra datos del caso, adjuntos descargables, y panel de resolución
  (aprobar / rechazar / derivar a auditoría) para operadores y admins.
-->
<template>
  <div class="auth-detail" v-loading="isLoading">
    <div v-if="authCase" class="auth-detail__body">
      <!-- Navegación de regreso -->
      <div
        class="auth-detail__back"
        @click="$router.push({ name: 'authorization' })"
      >
        <i class="el-icon-arrow-left"></i>
        <span>Autorizaciones</span>
      </div>

      <!-- Header del caso -->
      <div class="auth-detail__header">
        <div class="auth-detail__header-left">
          <span class="auth-detail__case-number"
            >#{{ authCase.caseNumber }}</span
          >
          <el-tag
            :type="statusType(authCase.status)"
            size="medium"
            style="margin-left: 12px"
          >
            {{ statusLabel(authCase.status) }}
          </el-tag>
        </div>
        <div class="auth-detail__header-right">
          <span class="auth-detail__date">{{
            formatDate(authCase.requestedAt)
          }}</span>
        </div>
      </div>

      <div class="auth-detail__grid">
        <!-- Datos del afiliado -->
        <div class="auth-detail__section">
          <div class="auth-detail__section-title">Afiliado</div>
          <div class="auth-detail__field">
            <span class="auth-detail__label">Nombre</span>
            <span
              >{{ authCase.memberSnapshot.lastName }},
              {{ authCase.memberSnapshot.firstName }}</span
            >
          </div>
          <div class="auth-detail__field">
            <span class="auth-detail__label">N° Afiliado</span>
            <span>{{ authCase.memberSnapshot.memberId || "-" }}</span>
          </div>
          <div class="auth-detail__field">
            <span class="auth-detail__label">Plan</span>
            <span>{{ authCase.memberSnapshot.planName || "-" }}</span>
          </div>
        </div>

        <!-- Datos del prestador (si origin=provider) -->
        <div v-if="authCase.origin === 'provider'" class="auth-detail__section">
          <div class="auth-detail__section-title">Prestador solicitante</div>
          <div class="auth-detail__field">
            <span class="auth-detail__label">Nombre</span>
            <span
              >{{ authCase.providerSnapshot.lastName }},
              {{ authCase.providerSnapshot.firstName }}</span
            >
          </div>
          <div class="auth-detail__field">
            <span class="auth-detail__label">CUIT</span>
            <span>{{ authCase.providerSnapshot.cuit || "-" }}</span>
          </div>
        </div>

        <!-- Datos de la prestación -->
        <div class="auth-detail__section">
          <div class="auth-detail__section-title">Prestación solicitada</div>
          <div class="auth-detail__field">
            <span class="auth-detail__label">Código</span>
            <span class="auth-detail__code">{{
              authCase.practiceSnapshot.code
            }}</span>
          </div>
          <div class="auth-detail__field">
            <span class="auth-detail__label">Nombre</span>
            <span>{{ authCase.practiceSnapshot.name }}</span>
          </div>
          <div class="auth-detail__field">
            <span class="auth-detail__label">Categoría</span>
            <span>{{ categoryLabel(authCase.practiceSnapshot.category) }}</span>
          </div>
          <div class="auth-detail__field">
            <span class="auth-detail__label">¿Requiere autorización?</span>
            <el-tag
              size="small"
              :type="authCase.requiresAuthorization ? 'danger' : 'success'"
            >
              {{ authCase.requiresAuthorization ? "Sí" : "No" }}
            </el-tag>
          </div>
          <div v-if="authCase.wasAudited" class="auth-detail__field">
            <span class="auth-detail__label">Revisión</span>
            <el-tag
              size="mini"
              type="info"
              style="
                font-size: 11px;
                font-family: 'Plus Jakarta Sans', sans-serif;
              "
            >
              <i class="el-icon-s-check"></i> Aprobado por auditoría
            </el-tag>
          </div>
        </div>

        <!-- Aprobación (si está aprobado o ejecutado) -->
        <div
          v-if="
            authCase.status === 'approved' || authCase.status === 'executed'
          "
          class="auth-detail__section"
        >
          <div class="auth-detail__section-title">Aprobación</div>
          <div class="auth-detail__field">
            <span class="auth-detail__label">Código de autorización</span>
            <span class="auth-detail__auth-code">{{
              authCase.authorizationCode
            }}</span>
          </div>
          <div class="auth-detail__field">
            <span class="auth-detail__label">Alcance</span>
            <span>{{
              authCase.approvalScope === "specific_provider"
                ? "Prestador específico"
                : "Cualquier prestador de la red"
            }}</span>
          </div>
          <div
            v-if="authCase.approvalScope === 'specific_provider'"
            class="auth-detail__field"
          >
            <span class="auth-detail__label">Prestador aprobado</span>
            <span
              >{{ authCase.approvedProviderSnapshot.lastName }},
              {{ authCase.approvedProviderSnapshot.firstName }}</span
            >
          </div>
          <div class="auth-detail__field">
            <span class="auth-detail__label">Vigencia</span>
            <span
              >{{ formatDate(authCase.approvedFrom) }} —
              {{ formatDate(authCase.approvedUntil) }}</span
            >
          </div>
          <div v-if="authCase.status === 'executed'" class="auth-detail__field">
            <span class="auth-detail__label">Ejecutada el</span>
            <span>{{ formatDate(authCase.executedAt) }}</span>
          </div>
        </div>
      </div>

      <!-- Observaciones -->
      <div v-if="authCase.observations" class="auth-detail__observations">
        <div class="auth-detail__section-title">Observaciones</div>
        <p>{{ authCase.observations }}</p>
      </div>

      <!-- Adjuntos -->
      <div
        v-if="authCase.attachments && authCase.attachments.length"
        class="auth-detail__attachments"
      >
        <div class="auth-detail__section-title">Documentación adjunta</div>
        <div class="auth-detail__attachment-list">
          <div
            v-for="(att, idx) in authCase.attachments"
            :key="att.key"
            class="auth-detail__attachment-item"
            @click="downloadAttachment(att.key)"
          >
            <i class="el-icon-document" />
            <span>Archivo {{ idx + 1 }}</span>
            <span class="auth-detail__attachment-size">{{
              formatSize(att.size)
            }}</span>
            <i class="el-icon-download" />
          </div>
        </div>
      </div>

      <!-- Si está en requires_audit mostrar aviso, no el panel de resolución -->
      <div
        v-if="authCase.status === 'requires_audit'"
        class="auth-detail__audit-notice"
      >
        <i class="el-icon-warning-outline"></i>
        Este caso está derivado a auditoría. La resolución la realiza el módulo
        de Auditoría.
      </div>

      <!-- Panel de resolución solo si está pending -->
      <div v-if="authCase.status === 'pending'" class="auth-detail__resolve">
        <div class="auth-detail__section-title">Resolver caso</div>

        <el-form
          ref="resolveForm"
          :model="resolveForm"
          :rules="resolveRules"
          label-position="top"
        >
          <el-form-item label="Decisión" prop="status">
            <el-radio-group
              v-model="resolveForm.status"
              @change="onDecisionChange"
            >
              <el-radio-button label="approved">Aprobar</el-radio-button>
              <el-radio-button label="requires_audit"
                >Derivar a auditoría</el-radio-button
              >
              <el-radio-button label="rejected">Rechazar</el-radio-button>
            </el-radio-group>
          </el-form-item>

          <!-- Si aprueba -->
          <template v-if="resolveForm.status === 'approved'">
            <el-form-item label="Alcance de la aprobación" prop="approvalScope">
              <el-radio-group
                v-model="resolveForm.approvalScope"
                @change="onScopeChange"
              >
                <el-radio label="any_provider"
                  >Cualquier prestador de la red</el-radio
                >
                <el-radio label="specific_provider"
                  >Prestador específico</el-radio
                >
              </el-radio-group>
            </el-form-item>

            <el-form-item
              v-if="resolveForm.approvalScope === 'specific_provider'"
              label="Prestador aprobado"
              prop="approvedProviderId"
            >
              <el-select
                v-model="resolveForm.approvedProviderId"
                filterable
                :loading="loadingApprovedProviders"
                placeholder="Seleccionar prestador..."
                style="width: 100%"
              >
                <el-option
                  v-for="p in approvedProviderOptions"
                  :key="p._id"
                  :label="`${p.lastName}, ${p.firstName}`"
                  :value="p._id"
                />
              </el-select>
              <div
                v-if="
                  approvedProviderOptions.length === 0 &&
                  !loadingApprovedProviders
                "
                class="auth-detail__no-providers"
              >
                No hay prestadores con contrato vigente para esta prestación.
              </div>
            </el-form-item>

            <el-row :gutter="16">
              <el-col :span="12">
                <el-form-item label="Válido desde" prop="approvedFrom">
                  <el-date-picker
                    v-model="resolveForm.approvedFrom"
                    type="date"
                    format="dd/MM/yyyy"
                    style="width: 100%"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="12">
                <el-form-item label="Válido hasta" prop="approvedUntil">
                  <el-date-picker
                    v-model="resolveForm.approvedUntil"
                    type="date"
                    format="dd/MM/yyyy"
                    style="width: 100%"
                  />
                </el-form-item>
              </el-col>
            </el-row>
          </template>

          <!-- Si deriva a auditoría -->
          <el-form-item
            v-if="resolveForm.status === 'requires_audit'"
            label="Notas para auditoría"
            prop="auditNotes"
          >
            <el-input
              v-model="resolveForm.auditNotes"
              type="textarea"
              :rows="3"
            />
          </el-form-item>

          <!-- Si rechaza -->
          <el-form-item
            v-if="resolveForm.status === 'rejected'"
            label="Motivo de rechazo"
            prop="rejectionReason"
          >
            <el-input
              v-model="resolveForm.rejectionReason"
              type="textarea"
              :rows="3"
            />
          </el-form-item>

          <div class="auth-detail__resolve-actions">
            <el-button
              :type="resolveButtonType"
              :loading="isResolving"
              :disabled="!resolveForm.status"
              @click="submitResolve"
            >
              Confirmar decisión
            </el-button>
          </div>
        </el-form>
      </div>

      <!-- Resultado de rechazo -->
      <div v-if="authCase.status === 'rejected'" class="auth-detail__rejection">
        <div class="auth-detail__section-title">Motivo de rechazo</div>
        <p>{{ authCase.rejectionReason }}</p>
      </div>

      <!-- Notas de auditoría -->
      <div
        v-if="authCase.status === 'requires_audit' && authCase.auditNotes"
        class="auth-detail__audit-notes"
      >
        <div class="auth-detail__section-title">Notas de auditoría</div>
        <p>{{ authCase.auditNotes }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import authorizationService from "@/services/authorization";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";

/** @component AuthorizationDetail */
export default {
  name: "AuthorizationDetail",

  data() {
    return {
      /** @type {Object|null} Caso cargado */
      authCase: null,
      /** @type {boolean} */
      isLoading: false,
      /** @type {boolean} */
      isResolving: false,
      /** @type {boolean} */
      loadingApprovedProviders: false,
      /** @type {Array} Prestadores disponibles para aprobación específica */
      approvedProviderOptions: [],
      /** @type {Object} Formulario de resolución */
      resolveForm: {
        status: "",
        approvalScope: "any_provider",
        approvedProviderId: "",
        approvedFrom: new Date(),
        approvedUntil: null,
        rejectionReason: "",
        auditNotes: "",
      },
      resolveRules: {
        status: [{ required: true, message: "Seleccioná una decisión" }],
        approvalScope: [{ required: true }],
        approvedUntil: [
          { required: true, message: "Ingresá la fecha de vencimiento" },
        ],
        rejectionReason: [
          { required: true, message: "Ingresá el motivo de rechazo" },
        ],
      },
    };
  },

  computed: {
    /**
     * @description Color del botón de resolución según la decisión seleccionada.
     * @returns {string}
     */
    resolveButtonType() {
      const map = {
        approved: "success",
        requires_audit: "warning",
        rejected: "danger",
      };
      return map[this.resolveForm.status] || "primary";
    },
  },

  created() {
    this.$bus.$emit("update-title", "Autorización");
    this.loadCase();
  },

  destroyed() {
    this.$bus.$emit("update-title", undefined);
  },

  methods: {
    /** @description Carga el caso por ID de la ruta. */
    async loadCase() {
      this.isLoading = true;
      try {
        this.authCase = await authorizationService.getById(
          this.$route.params.id
        );
        this.$bus.$emit(
          "update-title",
          `Autorización #${this.authCase.caseNumber}`
        );
      } catch (e) {
        this.$message.error("Error al cargar el caso");
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * @description Al cambiar la decisión, si es aprobación con prestador específico
     * carga la lista de prestadores habilitados para la prestación.
     */
    onDecisionChange() {
      if (
        this.resolveForm.status === "approved" &&
        this.resolveForm.approvalScope === "specific_provider"
      ) {
        this.loadApprovedProviders();
      }
    },

    /**
     * @description Al cambiar el alcance, carga prestadores si es específico.
     */
    onScopeChange() {
      if (this.resolveForm.approvalScope === "specific_provider") {
        this.loadApprovedProviders();
      }
    },

    /**
     * @description Carga prestadores con contrato activo para la prestación del caso.
     */
    async loadApprovedProviders() {
      if (!this.authCase) return;
      this.loadingApprovedProviders = true;
      try {
        this.approvedProviderOptions =
          await authorizationService.getProvidersForPractice(
            this.authCase.practice
          );
      } catch (e) {
        this.$message.error("Error al cargar prestadores");
      } finally {
        this.loadingApprovedProviders = false;
      }
    },

    /**
     * @description Valida y envía la resolución del caso.
     */
    async submitResolve() {
      const valid = await this.$refs.resolveForm.validate().catch(() => false);
      if (!valid) return;

      this.isResolving = true;
      try {
        const payload = { status: this.resolveForm.status };

        if (this.resolveForm.status === "approved") {
          payload.approvalScope = this.resolveForm.approvalScope;
          payload.approvedFrom = this.resolveForm.approvedFrom;
          payload.approvedUntil = this.resolveForm.approvedUntil;
          if (this.resolveForm.approvalScope === "specific_provider") {
            payload.approvedProviderId = this.resolveForm.approvedProviderId;
          }
        }

        if (this.resolveForm.status === "rejected") {
          payload.rejectionReason = this.resolveForm.rejectionReason;
        }

        if (this.resolveForm.status === "requires_audit") {
          payload.auditNotes = this.resolveForm.auditNotes;
        }

        this.authCase = await authorizationService.resolve(
          this.authCase._id,
          payload
        );
        this.$message.success("Caso resuelto correctamente");
      } catch (e) {
        this.$message.error("Error al resolver el caso");
      } finally {
        this.isResolving = false;
      }
    },

    /**
     * @description Descarga un adjunto del caso via signed URL.
     * @param {string} key - Key S3 del archivo
     */
    async downloadAttachment(key) {
      try {
        const { url } = await authorizationService.getFileUrl(
          this.authCase._id,
          key
        );
        window.open(url, "_blank");
      } catch (e) {
        this.$message.error("Error al obtener el archivo");
      }
    },

    /**
     * @description Devuelve etiqueta legible de la categoría de prestación
     * @param {string} category
     * @returns {string}
     */
    categoryLabel(category) {
      const labels = {
        consultation: "Consulta",
        lab: "Laboratorio",
        imaging: "Imágenes",
        surgery: "Cirugía",
        dental: "Odontología",
        mental_health: "Salud mental",
        rehabilitation: "Rehabilitación",
        emergency: "Urgencias",
        hospitalization: "Internación",
        maternity: "Maternidad",
        medication: "Medicación",
        supply: "Insumos",
        prosthesis: "Prótesis",
        other: "Otros",
      };
      return labels[category] || category || "-";
    },

    /**
     * @param {string} status
     * @returns {string}
     */
    statusType(status) {
      const map = {
        pending: "warning",
        approved: "success",
        executed: "info",
        requires_audit: "danger",
        rejected: "danger",
      };
      return map[status] || "info";
    },

    /**
     * @param {string} status
     * @returns {string}
     */
    statusLabel(status) {
      const map = {
        pending: "Pendiente",
        approved: "Aprobada",
        executed: "Ejecutada",
        requires_audit: "Requiere auditoría",
        rejected: "Rechazada",
      };
      return map[status] || status;
    },

    /**
     * @param {string} dateStr
     * @returns {string}
     */
    formatDate(dateStr) {
      if (!dateStr) return "-";
      return format(parseISO(dateStr), "dd/MM/yyyy HH:mm", { locale: es });
    },

    /**
     * @description Formatea tamaño de archivo en KB/MB.
     * @param {number} bytes
     * @returns {string}
     */
    formatSize(bytes) {
      if (!bytes) return "";
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
  },
};
</script>

<style scoped>
.auth-detail__back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #002d5e;
  cursor: pointer;
  margin-bottom: 20px;
  opacity: 0.7;
  transition: opacity 0.15s;
  font-family: "Plus Jakarta Sans", sans-serif;
}
.auth-detail__back:hover {
  opacity: 1;
}

.auth-detail__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 28px;
}

.auth-detail__case-number {
  font-size: 22px;
  font-weight: 700;
  color: #002d5e;
}

.auth-detail__date {
  font-size: 13px;
  color: #8492a6;
}

.auth-detail__grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  margin-bottom: 28px;
}

.auth-detail__section {
  background: #f8f9fc;
  border-radius: 10px;
  padding: 20px;
}

.auth-detail__section-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8492a6;
  margin-bottom: 14px;
}

.auth-detail__field {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid #eef0f5;
  font-size: 14px;
  color: #002d5e;
}

.auth-detail__field:last-child {
  border-bottom: none;
}

.auth-detail__label {
  color: #8492a6;
  font-size: 13px;
}

.auth-detail__code {
  font-weight: 700;
  color: #05e5ba;
}

.auth-detail__observations,
.auth-detail__rejection,
.auth-detail__audit-notes {
  background: #f8f9fc;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 24px;
  font-size: 14px;
  color: #002d5e;
}

.auth-detail__attachments {
  margin-bottom: 24px;
}

.auth-detail__attachment-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.auth-detail__attachment-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  background: #f0f9ff;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  color: #002d5e;
  transition: background 0.2s;
}

.auth-detail__attachment-item:hover {
  background: #e0f3ff;
}

.auth-detail__attachment-size {
  color: #8492a6;
  font-size: 12px;
  margin-left: auto;
  margin-right: 8px;
}

.auth-detail__resolve {
  background: #fff;
  border: 1px solid #e8eaf0;
  border-radius: 12px;
  padding: 24px;
  margin-top: 28px;
}

.auth-detail__resolve-actions {
  margin-top: 8px;
}

.auth-detail__no-providers {
  font-size: 12px;
  color: #e6a23c;
  margin-top: 6px;
}

.auth-detail__auth-code {
  font-family: monospace;
  font-size: 15px;
  font-weight: 700;
  color: #002d5e;
  background: #f0f2f7;
  padding: 4px 12px;
  border-radius: 6px;
  letter-spacing: 1px;
}

.auth-detail__audit-notice {
  background: #faeeda;
  border: 1px solid #f5c47a;
  border-radius: 8px;
  padding: 14px 18px;
  color: #854f0b;
  font-size: 13px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
}
</style>
